/**
 * 3D electrical raceways for the Viewer: conduit centerlines drawn as line
 * segments (colored by raceway kind — EMT/IMC/RMC steel greys, PVC warm,
 * cable tray green; see ../mep/raceways RACEWAY_COLORS) plus small box glyphs
 * for pull/junction boxes. A THIN render of the engine's opt-in raceway
 * Output — no routing/sizing logic lives here.
 *
 * Structure mirrors {@link DuctLayer}: conduit runs are merged into ONE
 * LineSegments BufferGeometry per kind per storey (`conduits:<kind>:<storey>`),
 * pull boxes are an InstancedMesh per sizing basis (`pullboxes:<basis>`), so a
 * whole storey renders in a handful of draw calls. Owns a THREE.Group the
 * Viewer adds to its scene; the per-model alignment offset is applied on
 * `group` exactly like DuctLayer/DeviceLayer (via align.ts).
 *
 * Coordinates: server data is plan meters (x east, y north, z up); three.js is
 * y-up, so everything maps through planToThree (x, z, y) — same as DuctLayer.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import {
  conduitLineVertices,
  racewayColor,
  type LayerConduit,
  type LayerPullBox,
} from "../mep/raceways";
import { planToThree } from "../mep/ducts";

/** Pull-box glyph edge (meters) — a small cube marker at the node. */
const PULL_BOX_SIZE = 0.22;

function conduitLineGeometry(conduit: LayerConduit): THREE.BufferGeometry | null {
  const verts = conduitLineVertices(conduit.centerline);
  if (verts.length === 0) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
  return geometry;
}

export class ConduitLayer {
  /** Added to the Viewer's scene; everything the layer renders lives here. */
  readonly group = new THREE.Group();

  private conduits: LayerConduit[] = [];
  private pullBoxes: LayerPullBox[] = [];
  private storeyFilter: string | null = null;
  private built: (THREE.LineSegments | THREE.InstancedMesh)[] = [];

  // Shared resources reused across rebuilds (filter/visibility toggles):
  // line materials by color-key, the pull-box geometry, and box materials.
  // Disposed only in dispose(); clear() never disposes these. Per-rebuild
  // merged line geometry IS unique and IS disposed by clear().
  private lineMatCache = new Map<string, THREE.LineBasicMaterial>();
  private boxGeo: THREE.BoxGeometry | null = null;
  private boxMat: THREE.MeshStandardMaterial | null = null;

  constructor() {
    this.group.name = "istari-conduit-layer";
  }

  /** Replace all raceways (called after generation/regeneration). */
  setRaceways(
    conduits: readonly LayerConduit[],
    pullBoxes: readonly LayerPullBox[] = [],
  ): void {
    this.conduits = [...conduits];
    this.pullBoxes = [...pullBoxes];
    this.rebuild();
  }

  /** Toggle the whole raceway layer (independent of ducts/devices). */
  setLayerVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  get layerVisible(): boolean {
    return this.group.visible;
  }

  /**
   * Restrict raceways to one storey (`null` shows all). Conduits/pull-boxes
   * carry their route's real storey id, so the filter is an exact match.
   */
  setStoreyFilter(storeyId: string | null): void {
    this.storeyFilter = storeyId;
    this.rebuild();
  }

  /** Names of the meshes currently built, sorted (deterministic; tests/UI). */
  meshNames(): string[] {
    return this.built.map((m) => m.name).sort();
  }

  /** Rendered instance count for one pull-box mesh name (0 if absent). */
  instanceCount(name: string): number {
    const mesh = this.built.find((m) => m.name === name);
    return mesh instanceof THREE.InstancedMesh ? mesh.count : 0;
  }

  private passesFilter(storeyId: string): boolean {
    if (this.storeyFilter === null) return true;
    return storeyId === this.storeyFilter;
  }

  private rebuild(): void {
    this.clear();
    this.buildConduits();
    this.buildPullBoxes();
  }

  /** One merged LineSegments geometry per kind per storey (see module docs). */
  private buildConduits(): void {
    const groups = new Map<string, THREE.BufferGeometry[]>();
    for (const conduit of this.conduits) {
      if (!this.passesFilter(conduit.storey_id)) continue;
      const geometry = conduitLineGeometry(conduit);
      if (!geometry) continue;
      const key = `conduits:${conduit.kind}:${conduit.storey_id ?? "none"}`;
      const list = groups.get(key) ?? [];
      list.push(geometry);
      groups.set(key, list);
    }
    for (const key of [...groups.keys()].sort()) {
      const parts = groups.get(key)!;
      const merged = mergeGeometries(parts, false);
      for (const part of parts) part.dispose();
      if (!merged) continue;
      const kind = key.split(":")[1];
      const mesh = new THREE.LineSegments(
        merged,
        this.cachedLineMaterial(kind),
      );
      mesh.name = key;
      this.add(mesh);
    }
  }

  private cachedLineMaterial(kind: string): THREE.LineBasicMaterial {
    let mat = this.lineMatCache.get(kind);
    if (!mat) {
      mat = new THREE.LineBasicMaterial({ color: new THREE.Color(racewayColor(kind)) });
      this.lineMatCache.set(kind, mat);
    }
    return mat;
  }

  /** One InstancedMesh of box glyphs per sizing basis. */
  private buildPullBoxes(): void {
    const byBasis = new Map<string, LayerPullBox[]>();
    for (const box of this.pullBoxes) {
      if (!this.passesFilter(box.storey_id)) continue;
      const list = byBasis.get(box.sizing_basis) ?? [];
      list.push(box);
      byBasis.set(box.sizing_basis, list);
    }
    this.boxGeo ??= new THREE.BoxGeometry(PULL_BOX_SIZE, PULL_BOX_SIZE, PULL_BOX_SIZE);
    this.boxMat ??= new THREE.MeshStandardMaterial({
      color: new THREE.Color(0xf2c14e), // amber node marker
      metalness: 0.2,
      roughness: 0.7,
    });
    const matrix = new THREE.Matrix4();
    for (const basis of [...byBasis.keys()].sort()) {
      const list = byBasis.get(basis)!;
      const mesh = new THREE.InstancedMesh(this.boxGeo, this.boxMat, list.length);
      mesh.name = `pullboxes:${basis}`;
      list.forEach((box, i) => {
        matrix.makeTranslation(...planToThree([box.x_m, box.y_m, box.z_m]));
        mesh.setMatrixAt(i, matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      this.add(mesh);
    }
  }

  private add(mesh: THREE.LineSegments | THREE.InstancedMesh): void {
    this.built.push(mesh);
    this.group.add(mesh);
  }

  private clear(): void {
    for (const mesh of this.built) {
      this.group.remove(mesh);
      // Line geometry is unique per rebuild — dispose it. Materials + the
      // pull-box geometry are shared caches, disposed only in dispose().
      if (mesh instanceof THREE.LineSegments) {
        mesh.geometry.dispose();
      } else {
        mesh.dispose(); // frees the InstancedMesh's own instance buffers
      }
    }
    this.built = [];
  }

  dispose(): void {
    this.clear();
    for (const mat of this.lineMatCache.values()) mat.dispose();
    this.lineMatCache.clear();
    this.boxGeo?.dispose();
    this.boxGeo = null;
    this.boxMat?.dispose();
    this.boxMat = null;
    this.conduits = [];
    this.pullBoxes = [];
  }
}
